import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useField } from "formik";
import { FC, useMemo } from "react";

import __ from "web/Layout/Translations";

import {
  DEFAULT_PHONE_PREFIX,
  getPhonePrefixOptions,
} from "web/utils/page/product/subscription/forms/getPhonePrefixOptions";

import type { PhoneWithPrefixField } from "web/types/Subscription";

interface IPhoneWithPrefixFormProps {
  field: PhoneWithPrefixField;
  isMobile: boolean;
  tooltipMessage?: string;
  lang?: {
    key: string;
    code: string;
  };
}

const labels = {
  pl: {
    phoneNumber: "Wpisz numer telefonu",
  },
  en: {
    phoneNumber: "Enter phone number",
  },
};

const PhoneWithPrefixFormMUI: FC<IPhoneWithPrefixFormProps> = ({
  field,
  isMobile,
  tooltipMessage,
  lang = { key: "pl_PL", code: "pl" },
}) => {
  const langLabels = labels[lang.code as keyof typeof labels] || labels.pl;

  const prefixFieldName = `${field.id}.prefix`;
  const phoneNumberFieldName = `${field.id}.phoneNumber`;

  const [prefixField, , prefixHelpers] = useField(prefixFieldName);
  const [phoneField, phoneMeta, phoneHelpers] = useField(phoneNumberFieldName);

  const prefixOptions = useMemo(() => getPhonePrefixOptions(), []);

  const isPhoneError = phoneMeta.touched && Boolean(phoneMeta.error);

  const handlePrefixChange = (e: SelectChangeEvent<string>) => {
    prefixHelpers.setValue(e.target.value);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only digits
    const value = e.target.value.replace(/[^0-9]/g, "");
    phoneHelpers.setValue(value);
  };

  const labelElement = field.label && (
    <Typography variant="body2" sx={{ mb: 1 }}>
      {field.label}
    </Typography>
  );

  return (
    <Box sx={{ mt: 2, maxWidth: "512px" }}>
      {labelElement &&
        (tooltipMessage ? (
          <Tooltip title={tooltipMessage} placement="top" arrow>
            <Box>{labelElement}</Box>
          </Tooltip>
        ) : (
          labelElement
        ))}
      <Box
        sx={{
          display: "flex",
          gap: 1,
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        {/* Prefix Select */}
        <FormControl
          sx={{
            minWidth: isMobile ? "100%" : "100px",
            maxWidth: isMobile ? "100%" : "100px",
          }}
        >
          <InputLabel id={`${prefixFieldName}-label`} shrink>
            {/* Empty label - prefix selector doesn't need a visible label */}
          </InputLabel>
          <Select
            labelId={`${prefixFieldName}-label`}
            value={prefixField.value || DEFAULT_PHONE_PREFIX}
            onChange={handlePrefixChange}
            onBlur={() => prefixHelpers.setTouched(true)}
            data-t1={`${field.id}-prefix`}
            size="medium"
            sx={{
              "& .MuiSelect-select": {
                paddingTop: "16.5px",
                paddingBottom: "16.5px",
                marginLeft: "10px",
                py: "16.5px",
              },
            }}
          >
            {prefixOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Phone Number Input */}
        <FormControl sx={{ flex: 1 }} error={isPhoneError}>
          <TextField
            value={phoneField.value || ""}
            onChange={handlePhoneChange}
            onBlur={() => phoneHelpers.setTouched(true)}
            placeholder={__(langLabels.phoneNumber)}
            data-t1={`${field.id}-phoneNumber`}
            error={isPhoneError}
            helperText={isPhoneError ? phoneMeta.error : ""}
            slotProps={{
              inputLabel: {
                shrink: true,
              },
            }}
            sx={{
              "& .MuiFormHelperText-root": {
                ml: 0,
              },
            }}
          />
        </FormControl>
      </Box>
    </Box>
  );
};

export default PhoneWithPrefixFormMUI;
